import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import ShareButton from "../components/ShareButton";
import { toast } from "sonner";
import { Save, Share2, Eye, EyeOff } from "lucide-react";

const INDUSTRIES = ["Technology", "Retail", "E-commerce", "Hospitality", "Food & Beverage", "Construction", "Manufacturing", "Healthcare", "Education", "Logistics", "Real Estate", "Agriculture", "Services", "Beauty & Wellness", "Automotive"];
const COUNTRIES = ["UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt"];
const INVESTOR_TYPES = ["Angel", "Venture Capital", "Private Equity", "Family Office", "Strategic", "Corporate VC", "Other"];
const DEAL_TYPES = ["Acquisition", "Equity Investment", "Debt Financing", "Joint Venture", "Strategic Partnership", "M&A"];
const STAGES = ["Idea", "Pre-Seed", "Seed", "Series A", "Series B+", "Mature / Profitable"];
const VISIBILITY = [
  { v: "public", l: "Public — anyone can find me" },
  { v: "link-only", l: "Link only — only people with my link" },
  { v: "private", l: "Private — hidden from all" },
];

export default function ProfileTab() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/profile").then(r => setProfile(r.data));
  }, [user]);

  if (!profile) return <div className="text-slate-500">Loading profile...</div>;

  const set = (patch) => setProfile(p => ({ ...p, ...patch }));
  const toggle = (key, v) => set({ [key]: (profile[key] || []).includes(v) ? profile[key].filter(x => x !== v) : [...(profile[key] || []), v] });

  const save = async () => {
    setSaving(true);
    try {
      const allowed = ["full_name", "company_name", "phone", "country", "bio", "investor_type", "industries", "countries", "budget_min", "budget_max", "deal_types", "business_stage", "visibility"];
      const payload = Object.fromEntries(allowed.filter(k => profile[k] !== undefined).map(k => [k, profile[k]]));
      await api.put("/profile", payload);
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const publicUrl = `${window.location.origin}/u/${profile.id}`;
  const isInvestor = profile.role === "investor" || profile.role === "buyer";

  return (
    <div data-testid="profile-tab" className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h2 className="font-bold text-2xl">Your profile</h2>
          <p className="text-slate-500 text-sm">This is what others see when they view or share your profile.</p>
        </div>
        <div className="flex gap-2">
          <a href={`/u/${profile.id}`} target="_blank" rel="noopener noreferrer" className="dv-btn dv-btn-secondary dv-btn-sm" data-testid="view-public-profile-btn">
            <Eye size={14} /> View public profile
          </a>
          <ShareButton url={publicUrl} title={`${profile.full_name || profile.company_name || "DealVest member"} on DealVest`} text={profile.bio || ""} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 dv-shadow-soft space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="dv-label">Full name</label><input className="dv-input" value={profile.full_name || ""} onChange={e => set({ full_name: e.target.value })} data-testid="profile-full-name" /></div>
          <div><label className="dv-label">Company name</label><input className="dv-input" value={profile.company_name || ""} onChange={e => set({ company_name: e.target.value })} data-testid="profile-company-name" /></div>
          <div><label className="dv-label">Phone</label><input className="dv-input" value={profile.phone || ""} onChange={e => set({ phone: e.target.value })} /></div>
          <div><label className="dv-label">Country</label>
            <select className="dv-input" value={profile.country || ""} onChange={e => set({ country: e.target.value })}>
              <option value="">Select...</option>
              {COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="dv-label">Bio / Investment focus</label>
          <textarea rows="3" className="dv-input is-textarea" placeholder="Short intro for your public profile..." value={profile.bio || ""} onChange={e => set({ bio: e.target.value })} data-testid="profile-bio" />
        </div>

        {isInvestor && (
          <>
            <div className="border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-lg mb-4 text-slate-900">Investor preferences</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="dv-label">Investor type</label>
                  <select className="dv-input" value={profile.investor_type || ""} onChange={e => set({ investor_type: e.target.value })} data-testid="profile-investor-type">
                    <option value="">Select...</option>
                    {INVESTOR_TYPES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="dv-label">Business stage</label>
                  <select className="dv-input" value={profile.business_stage || ""} onChange={e => set({ business_stage: e.target.value })}>
                    <option value="">Any</option>
                    {STAGES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="dv-label">Min ticket (USD)</label><input type="number" className="dv-input" value={profile.budget_min || ""} onChange={e => set({ budget_min: Number(e.target.value) || null })} /></div>
                <div><label className="dv-label">Max ticket (USD)</label><input type="number" className="dv-input" value={profile.budget_max || ""} onChange={e => set({ budget_max: Number(e.target.value) || null })} /></div>
              </div>
            </div>

            <div>
              <label className="dv-label">Industries of interest</label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map(i => (
                  <button key={i} type="button" onClick={() => toggle("industries", i)}
                    className={`text-sm px-3 py-1.5 rounded-full border ${(profile.industries || []).includes(i) ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="dv-label">Preferred countries</label>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map(c => (
                  <button key={c} type="button" onClick={() => toggle("countries", c)}
                    className={`text-sm px-3 py-1.5 rounded-full border ${(profile.countries || []).includes(c) ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="dv-label">Preferred deal types</label>
              <div className="flex flex-wrap gap-2">
                {DEAL_TYPES.map(d => (
                  <button key={d} type="button" onClick={() => toggle("deal_types", d)}
                    className={`text-sm px-3 py-1.5 rounded-full border ${(profile.deal_types || []).includes(d) ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="border-t border-slate-100 pt-5">
          <label className="dv-label">Profile visibility</label>
          <div className="space-y-2">
            {VISIBILITY.map(v => (
              <label key={v.v} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${profile.visibility === v.v ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                <input type="radio" name="visibility" checked={profile.visibility === v.v} onChange={() => set({ visibility: v.v })} className="accent-blue-600" data-testid={`visibility-${v.v}`} />
                <span className="text-sm">{v.l}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-100">
          <button onClick={save} disabled={saving} className="dv-btn dv-btn-primary" data-testid="save-profile-btn">
            <Save size={15} /> {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
